import { connect } from 'react-redux';

import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { KNM_EVENT_SUBJECT } from 'CorePackage/MainModule/routes/routes';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import {
  getKnmIsErknmStatusCommentsExist,
  getKnmIsErknmStatusTypeWaitingCarryOut,
  getKnmIsErknmStatusRejected,
  getKnmDataErknmApproveAppeal,
  getKnmDataErknmApproveApproved,
  getKnmIsErknmStatusRejectAppealed,
  getKnmIsErknmStatusTypeCarriedOut,
  getKnmIsErknmStatusTypeCompleted,
  getKnmIsErknmStatusFillInProcess,
  getKnmIsErknmStatusReadyForApprove,
  getKnmIsEditableInStatusCommentsExist,
  getKnmIsNew,
  getKnmIsWatchOnly,
  getKnmIsNeedActSaveOrSign,
  getChangePlanReasonAfterConfirmEvent,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { getPlanListPlanErknmStatus } from 'PlanListPackage/MainModule/selectors/planListSelectors';
import { ApprovedTypesEnum } from 'APIPackage/KnmModule/enums/ApprovedTypesEnum';
import { Section } from 'UIPackage/FormModule/components/Section';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IKnmErknmFormChangePlanReason } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormChangePlanReason';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';

import { ErknmRequirements } from './ErknmRequirements';
import { ErknmChecklists } from './checklists/ErknmChecklists';
import { ErknmApproveDocs } from './ErknmApproveDocs';
import { ErknmPlaces } from './ErknmPlaces';
import { ErknmOrganizationDocuments } from './ErknmOrganizationDocuments';
import { IDictionaryKnoOrganizationSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { IDictionarySupervisionTypeSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { IKnmErknmFormRequirementNpa } from '../../MainModule/interfaces/IKnmErknmFormRequirementNpa';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { ErknmLinksOld } from './Links/ErknmLinksOld';
import { IKnmErknmFormReason } from '../../MainModule/interfaces/IKnmErknmFormReason';
import { IKnmErknmFormObject } from '../../MainModule/interfaces/IKnmErknmFormObject';
import { IKnmFormRiskIndicators } from '../../../KnmPackage/MainModule/interfaces/IKnmFormRiskIndicators';
import { ErknmRepeatViolation } from './ErknmRepeatViolation/ErknmRepeatViolation';
import { SelectPrescriptionNotice } from '../../MainModule/components/SelectPrescriptionNotice';
import {
  getKnmErknmIsPrescriptionFailed,
  getKnmErknmIsPrescriptionOldRules,
} from '../../MainModule/selectors/knmErknmSelectors';

interface IErknmEventSubjectProps {
  created: string | undefined;
  isChecklistsUsed: boolean | undefined;
  kindControl?: IDictionarySupervisionTypeSelectOption;
  kindKnmCode: string | undefined;
  knoOrganization?: IDictionaryKnoOrganizationSelectOption;
  reasonFieldValue?: IKnmErknmFormReason | undefined;
  requirementsNpa?: IKnmErknmFormRequirementNpa[];
  objects?: IKnmErknmFormObject[];
  riskIndicators?: IKnmFormRiskIndicators[];
}

interface IErknmEventSubjectStoreProps {
  isAvailableInStatusCommentsExist: boolean;
  isKnmStatusCommentsExist: boolean;
  isKnmStatusFillInProcess: boolean;
  isKnmStatusReadyForApprove: boolean;
  isKnmStatusRejectAppealed: boolean;
  isKnmStatusRejected: boolean;
  isKnmStatusTypeCarriedOut: boolean;
  isKnmStatusTypeCompleted: boolean;
  isKnmStatusTypeWaitingCarryOut: boolean;
  isNewKnm: boolean;
  isSupervisor: boolean;
  isWatchOnly: boolean;
  knmDataApproveAppeal: boolean | null | undefined;
  knmDataApproved: ApprovedTypesEnum | null | undefined;
  knmPlanStatus: ErknmPlanStatusesEnum | undefined;
  settings: IAdminSetting[];
  changePlanReasonAfterConfirm?: IKnmErknmFormChangePlanReason[];
  isNeedActSaveOrSign?: boolean;
  isPassportByCheckPrescriptionFailed: boolean | undefined;
  isPassportByCheckPrescriptionOldRules: boolean | undefined;
}

const ErknmEventSubjectWithoutStore = ({
  created,
  isChecklistsUsed,
  kindControl,
  kindKnmCode,
  knoOrganization,
  objects,
  riskIndicators,
  reasonFieldValue,
  requirementsNpa,
  isAvailableInStatusCommentsExist,
  isKnmStatusCommentsExist,
  isKnmStatusFillInProcess,
  isKnmStatusReadyForApprove,
  isKnmStatusRejectAppealed,
  isKnmStatusRejected,
  isKnmStatusTypeCarriedOut,
  isKnmStatusTypeCompleted,
  isKnmStatusTypeWaitingCarryOut,
  isNewKnm,
  isSupervisor,
  isWatchOnly,
  knmDataApproveAppeal,
  knmDataApproved,
  knmPlanStatus,
  settings,
  changePlanReasonAfterConfirm,
  isNeedActSaveOrSign = false,
  isPassportByCheckPrescriptionFailed,
  isPassportByCheckPrescriptionOldRules,
}: IErknmEventSubjectProps & IErknmEventSubjectStoreProps) => {
  const isChecklistsVisible =
    kindKnmCode === KnmKindCodesEnum.IV || kindKnmCode === KnmKindCodesEnum.RO || kindKnmCode === KnmKindCodesEnum.VP;
  const isOrganizationDocumentsVisible =
    kindKnmCode === KnmKindCodesEnum.MZ ||
    kindKnmCode === KnmKindCodesEnum.VK ||
    kindKnmCode === KnmKindCodesEnum.IV ||
    kindKnmCode === KnmKindCodesEnum.RO ||
    kindKnmCode === KnmKindCodesEnum.DP ||
    kindKnmCode === KnmKindCodesEnum.VP;

  const isActiveFieldPlanConsideredWithApprovedAppeal =
    knmDataApproved === ApprovedTypesEnum.APPROVED &&
    !!knmDataApproveAppeal &&
    knmPlanStatus === ErknmPlanStatusesEnum.CONSIDERED;

  const isRsnChngPpXxii = changePlanReasonAfterConfirm
    ? changePlanReasonAfterConfirm.some(item => item?.reasonChangeType?.code === 'RSN_CHNG_PP_XXII')
    : false;

  const isEnableBasicEventSubjectFields =
    isSupervisor &&
    (isNewKnm ||
      isKnmStatusFillInProcess ||
      isKnmStatusReadyForApprove ||
      isActiveFieldPlanConsideredWithApprovedAppeal ||
      isAvailableInStatusCommentsExist ||
      isRsnChngPpXxii);

  const isEnableDocumentsFields =
    isEnableBasicEventSubjectFields ||
    (isSupervisor &&
      (isNewKnm ||
        isKnmStatusTypeWaitingCarryOut ||
        isKnmStatusTypeCarriedOut ||
        isKnmStatusTypeCompleted ||
        (knmPlanStatus === ErknmPlanStatusesEnum.UNDER_CONSIDERATION &&
          (isKnmStatusTypeWaitingCarryOut || isKnmStatusRejected || isKnmStatusCommentsExist)) ||
        (knmPlanStatus === ErknmPlanStatusesEnum.CONSIDERED &&
          (isKnmStatusCommentsExist || isKnmStatusRejectAppealed))));

  const isShow22 = isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.KNM_VALIDATION_4_1_22_STARTS_FROM);

  const isEnablePrescriptionCheck = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_30_STARTS_FROM,
  );

  const ervkReasonUuid = reasonFieldValue?.ervkUuid;

  const isShowKnmLinks =
    !ervkReasonUuid && !isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.KNM_REASON_4_1_54_STARTS_FROM);

  const isPrescriptionCheck =
    reasonFieldValue?.reason?.type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK &&
    isPassportByCheckPrescriptionOldRules === false;

  return (
    <Section title="Предмет контрольного (надзорного) мероприятия" id={KNM_EVENT_SUBJECT}>
      {isPrescriptionCheck && <SelectPrescriptionNotice />}
      {isEnablePrescriptionCheck && (isPrescriptionCheck || isPassportByCheckPrescriptionFailed === true) ? (
        <ErknmRepeatViolation checkboxDisabled={!isKnmStatusFillInProcess && !isKnmStatusReadyForApprove} />
      ) : (
        <ErknmRequirements
          knoOrganization={knoOrganization}
          kindControl={kindControl}
          requirementsNpa={requirementsNpa}
          reasonFieldValue={reasonFieldValue}
          riskIndicators={riskIndicators}
          objects={objects}
        />
      )}
      <ErknmApproveDocs />
      {isShowKnmLinks && <ErknmLinksOld watchOnly={isWatchOnly} isEnabled={isEnableBasicEventSubjectFields} />}
      {isChecklistsVisible && (
        <ErknmChecklists isChecklistsUsed={isChecklistsUsed} kindKnmCode={kindKnmCode} knmPlanStatus={knmPlanStatus} />
      )}
      <ErknmPlaces isWatchOnly={isNeedActSaveOrSign} />
      {!isShow22 && isOrganizationDocumentsVisible && (
        <ErknmOrganizationDocuments isEnabled={isEnableDocumentsFields} />
      )}
    </Section>
  );
};

const mapStateToProps = (state: IRootState): IErknmEventSubjectStoreProps => ({
  isAvailableInStatusCommentsExist: getKnmIsEditableInStatusCommentsExist(state),
  isKnmStatusCommentsExist: getKnmIsErknmStatusCommentsExist(state),
  isKnmStatusFillInProcess: getKnmIsErknmStatusFillInProcess(state),
  isKnmStatusReadyForApprove: getKnmIsErknmStatusReadyForApprove(state),
  isKnmStatusRejectAppealed: getKnmIsErknmStatusRejectAppealed(state),
  isKnmStatusRejected: getKnmIsErknmStatusRejected(state),
  isKnmStatusTypeCarriedOut: getKnmIsErknmStatusTypeCarriedOut(state),
  isKnmStatusTypeCompleted: getKnmIsErknmStatusTypeCompleted(state),
  isKnmStatusTypeWaitingCarryOut: getKnmIsErknmStatusTypeWaitingCarryOut(state),
  isNewKnm: getKnmIsNew(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  isWatchOnly: getKnmIsWatchOnly(state),
  knmDataApproveAppeal: getKnmDataErknmApproveAppeal(state),
  knmDataApproved: getKnmDataErknmApproveApproved(state),
  knmPlanStatus: getPlanListPlanErknmStatus(state),
  settings: getAdminFetchSettings(state),
  isNeedActSaveOrSign: getKnmIsNeedActSaveOrSign(state),
  changePlanReasonAfterConfirm: getChangePlanReasonAfterConfirmEvent(state),
  isPassportByCheckPrescriptionFailed: getKnmErknmIsPrescriptionFailed(state),
  isPassportByCheckPrescriptionOldRules: getKnmErknmIsPrescriptionOldRules(state),
});

export const ErknmEventSubject = connect(mapStateToProps)(ErknmEventSubjectWithoutStore);
